import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adsApiCall } from "./api";
import { Sparkles, Loader2, Wand2, Layers, Clapperboard } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * ReelDirectorBar — one-line AI input that generates a complete animated reel.
 * Three modes: single-scene (default), batch (5 variants), multi-scene
 * storytelling (hook → problem → proof → cta, 3-5 scenes).
 *
 * Props:
 *   compact        : boolean — tighter chrome for embedded / rail use
 *   defaultFormat  : canvas format key (e.g. "instagram_reel")
 *   presetPrompt   : string — when non-empty, pre-fills the prompt input
 *                    (useful for showcase-click-to-try flow).
 *   presetMode     : "single" | "multi" | "batch" — starting mode.
 */
const EXAMPLE_KEYS = [
  { key: "rd_ex_yoga", en: "Yoga studio in Bratislava — first class free" },
  { key: "rd_ex_mugs", en: "Flash sale on ceramic mugs, 48 hours only" },
  { key: "rd_ex_saas", en: "Black Friday for my SaaS — 50% off annual plans" },
  { key: "rd_ex_brunch", en: "Weekend brunch at my restaurant — pancakes & mimosas" },
];

export default function ReelDirectorBar({
  compact = false,
  defaultFormat = "instagram_reel",
  presetPrompt = "",
  presetMode = "single",
}) {
  const { t } = useLanguage();
  const EXAMPLES = EXAMPLE_KEYS.map((e) => t[e.key] || e.en);
  const [prompt, setPrompt] = useState(presetPrompt || "");
  const [loading, setLoading] = useState(false);
  // mode: "single" | "batch" | "multi"
  const [mode, setMode] = useState(presetMode);
  const [sceneCount, setSceneCount] = useState(4);
  const [batchResults, setBatchResults] = useState(null);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  // Respond to parent-driven prompt changes (showcase-card click flow). We
  // also auto-switch to multi-scene mode since showcase entries are built
  // by the multi-scene director.
  useEffect(() => {
    if (presetPrompt) {
      setPrompt(presetPrompt);
      setMode("multi");
      setErr(null); setBatchResults(null);
    }
  }, [presetPrompt]);

  async function run() {
    const p = prompt.trim();
    if (p.length < 4) return;
    setLoading(true); setErr(null); setBatchResults(null);
    try {
      if (mode === "batch") {
        const res = await adsApiCall.aiBatchDirector({
          prompt: p, language: guessLang(p), format: defaultFormat, count: 5,
        });
        if (res?.variants?.length) {
          setBatchResults(res.variants);
        } else {
          throw new Error("Couldn't generate variants");
        }
      } else if (mode === "multi") {
        const res = await adsApiCall.aiMultiSceneDirector(
          { prompt: p, language: guessLang(p), format: defaultFormat },
          sceneCount,
        );
        if (res?.project_id) {
          navigate(`/ads-creator/editor/${res.project_id}`);
        } else if (res?.scenes?.length) {
          navigate(`/login?next=${encodeURIComponent("/ads-creator#director")}`);
        } else {
          throw new Error(res?.error || "Couldn't generate the multi-scene reel");
        }
      } else {
        const res = await adsApiCall.aiReelDirector({ prompt: p, language: guessLang(p), format: defaultFormat });
        if (res?.project_id) {
          navigate(`/ads-creator/editor/${res.project_id}`);
        } else if (res?.canvas_json) {
          navigate(`/login?next=${encodeURIComponent("/ads-creator#director")}`);
        } else {
          throw new Error(res?.error || "Couldn't generate the reel");
        }
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  const ctaLabel = loading
    ? (mode === "batch"
        ? (t.rd_cta_gen_batch || "Generating 5 variants… (~15s)")
        : mode === "multi"
          ? (t.rd_cta_gen_multi || "Directing {n}-scene story… (~{s}s)").replace("{n}", sceneCount).replace("{s}", Math.ceil(sceneCount * 3))
          : (t.rd_cta_gen_single || "Directing your reel… (~10s)"))
    : (mode === "batch"
        ? (t.rd_cta_batch || "Generate 5 variants")
        : mode === "multi"
          ? (t.rd_cta_multi || "Generate {n}-scene reel").replace("{n}", sceneCount)
          : (t.rd_cta_single || "Generate reel"));

  return (
    <div
      data-testid="ads-director-bar"
      style={{
        width: "100%",
        background: compact
          ? "var(--ads-bg)"
          : "linear-gradient(135deg, rgba(168,85,247,0.08), rgba(124,58,237,0.08))",
        border: "1px solid rgba(168,85,247,0.22)",
        borderRadius: 18,
        padding: compact ? 14 : "22px 22px 20px",
        boxShadow: compact ? "none" : "0 20px 60px rgba(124,58,237,0.15)",
      }}
    >
      {!compact && (
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 10px", borderRadius: 999,
          background: "rgba(168, 85, 247, 0.15)",
          color: "#c084fc", fontSize: 10, fontWeight: 800,
          letterSpacing: 0.8, textTransform: "uppercase",
          marginBottom: 10,
        }}>
          <Wand2 size={11} /> {t.rd_eyebrow || "AI REEL DIRECTOR · NEW"}
        </div>
      )}
      {!compact && (
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 12, color: "var(--ads-text)" }}>
          {t.rd_headline || "Describe your ad in one sentence. Get a shippable reel."}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") run(); }}
          placeholder={EXAMPLES[0]}
          data-testid="ads-director-input"
          style={{
            flex: "1 1 320px",
            minWidth: 0,
            padding: "14px 16px",
            background: "var(--ads-bg)",
            border: "1px solid var(--ads-border)",
            borderRadius: 12,
            color: "var(--ads-text)",
            fontSize: 15,
            outline: "none",
          }}
        />
        <button
          onClick={run}
          disabled={loading || prompt.trim().length < 4}
          data-testid="ads-director-run"
          className="ads-btn ads-btn-primary"
          style={{
            padding: "14px 22px",
            opacity: loading || prompt.trim().length < 4 ? 0.55 : 1,
            cursor: loading || prompt.trim().length < 4 ? "not-allowed" : "pointer",
            fontSize: 15,
          }}
        >
          {loading
            ? <><Loader2 size={16} className="animate-spin" /> {ctaLabel}</>
            : <><Sparkles size={16} /> {ctaLabel}</>}
        </button>
      </div>

      {/* Mode selector — single / multi-scene / batch */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}
           data-testid="ads-director-mode-row">
        <ModeChip label={t.rd_mode_single || "Single scene"} icon={<Sparkles size={11} />} active={mode === "single"}
                  onClick={() => setMode("single")} testid="ads-director-mode-single" />
        <ModeChip label={t.rd_mode_multi || "Multi-scene story"} icon={<Clapperboard size={11} />} active={mode === "multi"}
                  onClick={() => setMode("multi")} testid="ads-director-mode-multi" badge="NEW" />
        <ModeChip label={t.rd_mode_batch || "Batch (5 variants)"} icon={<Layers size={11} />} active={mode === "batch"}
                  onClick={() => setMode("batch")} testid="ads-director-mode-batch" />
        {mode === "multi" && (
          <div style={{ display: "inline-flex", gap: 4, alignItems: "center", marginLeft: 2 }}
               data-testid="ads-director-scene-count-row">
            <span style={{ color: "var(--ads-text-dim)", fontSize: 11 }}>{t.rd_scenes_label || "Scenes:"}</span>
            {[3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setSceneCount(n)}
                data-testid={`ads-director-scene-count-${n}`}
                style={{
                  padding: "3px 10px", fontSize: 11, fontWeight: 800,
                  border: `1px solid ${sceneCount === n ? "#c084fc" : "rgba(255,255,255,0.1)"}`,
                  borderRadius: 6, cursor: "pointer",
                  background: sceneCount === n ? "rgba(168,85,247,0.2)" : "transparent",
                  color: sceneCount === n ? "#c084fc" : "var(--ads-text-dim)",
                }}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </div>

      {!compact && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }} data-testid="ads-director-examples">
          {EXAMPLES.slice(1).map((e, i) => (
            <button key={i}
              onClick={() => setPrompt(e)}
              className="ads-chip"
              style={{ fontSize: 11 }}
              data-testid={`ads-director-example-${i}`}
            >
              {e}
            </button>
          ))}
        </div>
      )}

      {err && (
        <div data-testid="ads-director-err"
             style={{ marginTop: 12, color: "#fca5a5", fontSize: 13 }}>
          {String(err)}
        </div>
      )}

      {batchResults && (
        <div data-testid="ads-director-batch-results" style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: "var(--ads-text-dim)", marginBottom: 10 }}>
            {(t.rd_variants_done || "{n} variants generated. Click one to open in editor — the others are saved in My Ads.").replace("{n}", batchResults.length)}
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
          }}>
            {batchResults.map((v, i) => (
              <button
                key={v.project_id || i}
                onClick={() => v.project_id
                  ? navigate(`/ads-creator/editor/${v.project_id}`)
                  : navigate("/login")}
                data-testid={`ads-director-variant-${i}`}
                style={{
                  padding: 0,
                  background: v.canvas_json?.background || "#111",
                  border: "1px solid var(--ads-border)",
                  borderRadius: 12,
                  overflow: "hidden",
                  cursor: "pointer",
                  position: "relative",
                  aspectRatio: `${v.width} / ${v.height}`,
                }}
              >
                <BatchPreview canvas={v.canvas_json} />
                <div style={{
                  position: "absolute", top: 6, left: 6,
                  background: "rgba(0,0,0,0.6)", color: "#fff",
                  padding: "2px 8px", borderRadius: 999, fontSize: 9, fontWeight: 800,
                  letterSpacing: 0.8, textTransform: "uppercase",
                }}>
                  {v.angle || "variant"}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Very lightweight heuristic — good enough for the top 3 languages on the platform.
// Server will ultimately generate in whatever language is passed.
function guessLang(s) {
  const t = s.toLowerCase();
  if (/[áäčďéíĺľňóôŕšťúýž]/.test(t)) return "sk";
  if (/[ěščřžýáíé]/.test(t)) return "cs";
  if (/[äöüß]/.test(t)) return "de";
  if (/[ąćęłńóśźż]/.test(t)) return "pl";
  return "en";
}

/** Pill-shaped mode toggle used by the director bar. */
function ModeChip({ label, icon, active, onClick, testid, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "5px 12px",
        borderRadius: 999, cursor: "pointer",
        fontSize: 11, fontWeight: 800, letterSpacing: "0.02em",
        border: `1px solid ${active ? "#c084fc" : "rgba(255,255,255,0.1)"}`,
        background: active ? "rgba(168,85,247,0.18)" : "transparent",
        color: active ? "#e9d5ff" : "var(--ads-text-dim)",
        transition: "background 120ms ease, border-color 120ms ease",
      }}
    >
      {icon}
      {label}
      {badge && (
        <span style={{
          marginLeft: 4, padding: "1px 5px", borderRadius: 4,
          background: "linear-gradient(135deg,#a855f7,#7c3aed)",
          color: "#fff", fontSize: 9, fontWeight: 900, letterSpacing: 0.5,
        }}>{badge}</span>
      )}
    </button>
  );
}

/** Inline SVG preview of a batch variant canvas — no Fabric. */
function BatchPreview({ canvas }) {
  if (!canvas) return null;
  const w = canvas.width || 1080, h = canvas.height || 1920;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100%"
         preserveAspectRatio="xMidYMid slice">
      <rect width={w} height={h} fill={canvas.background || "#000"} />
      {(canvas.objects || []).map((o, i) => {
        if (o.type === "rect") {
          return <rect key={i}
            x={o.left || 0} y={o.top || 0}
            width={o.width || 0} height={o.height || 0}
            fill={o.fill || "#000"} opacity={o.opacity ?? 1}
            rx={o.rx || 0} ry={o.ry || 0}
            transform={o.angle ? `rotate(${o.angle} ${(o.left || 0) + (o.width || 0) / 2} ${(o.top || 0) + (o.height || 0) / 2})` : undefined}
          />;
        }
        if (o.type === "circle") {
          return <circle key={i}
            cx={(o.left || 0) + (o.radius || 0)}
            cy={(o.top || 0) + (o.radius || 0)}
            r={o.radius || 0}
            fill={o.fill || "#000"} opacity={o.opacity ?? 1}
            stroke={o.stroke || undefined} strokeWidth={o.strokeWidth || undefined}
          />;
        }
        if (o.type === "textbox") {
          const lines = (o.text || "").split("\n").slice(0, 4);
          return lines.map((ln, li) => (
            <text key={`${i}-${li}`}
              x={(o.left || 0) + (o.textAlign === "center" ? (o.width || 0) / 2 : 0)}
              y={(o.top || 0) + (o.fontSize || 16) * (1 + li * 1.04)}
              fontFamily={o.fontFamily || "Inter"}
              fontWeight={o.fontWeight || 400}
              fontSize={o.fontSize || 20}
              fill={o.fill || "#fff"}
              opacity={o.opacity ?? 1}
              textAnchor={o.textAlign === "center" ? "middle" : "start"}
            >
              {ln.slice(0, 42)}
            </text>
          ));
        }
        return null;
      })}
    </svg>
  );
}
